import React, { useContext, useEffect, useRef, useState } from 'react';
import { Subscription } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import ImageSlider from '@components/products/ProductImageSlider';
import Colors from '@components/shared/Colors';
import ShoppingHero from '@components/shared/design/ShoppingHero';
import HeaderFeedback from '@components/shared/HeaderFeedback';
import HeaderIcons from '@components/shared/HeaderIcons';
import LinkTranslated from '@components/shared/LinkTranslated';
import StickyBanner from '@components/shared/StickyBanner';
import Text from '@components/shared/Text';
import TextLinkButton from '@components/shared/TextLinkButton';
import TwoColumn from '@components/shared/TwoColumn';
import { H1 } from '@components/shared/Typography';
import useDesktop from '@components/shared/useDesktop';
import useFetch from '@components/useFetch';
import { NAVBAR_HEIGHT } from '@constants';
import { CartItem, ProductCode } from '@contracts';
import BrightFridayBanner from './BrightFridayBanner';
import FlexPDPHoverOverlay from './FlexPDPHoverOverlay';
import NurseConsultationModule from './NurseConsultationModule';
import ProductHeroTrustPilot from './ProductHeroTrustpilot';
import ProductPromoRibbon from './ProductPromoRibbon';
import ProductPromotion from './ProductPromotion';
import { useShoppingContext } from './ProductShoppingContext';
import WaitlistPaymentRender from './WaitlistPaymentRender';

interface Props {}

const SharedProductHeader: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { firebaseAuth } = state;
  const fetch = useFetch();
  const ref = useRef(null);
  const divRef = useRef(null);
  const [showReadMore, setShowReadMore] = useState(false);
  const largeDesktop = useDesktop(1400);
  const columnGap = largeDesktop ? '90px' : '50px';

  const {
    productId,
    productHeaderData,
    setConfigVariant,
    setSubscriptionId,
    isUsingNewDesignSystem,
    orderedSlides,
    topSubtitleText,
    headingText,
    bodyText,
    subtextText,
    filteredLinks,
    showNurseConsultation,
    stickyBanner,
    scrollHeight,
    hasFeedbackView
  } = useShoppingContext();
  const { klaviyoId, productCode, textColor, background, promoRibbon, trustpilot, subtitle, icons, verticalIcons, feedback } = productHeaderData;

  useEffect(() => {
    const PADS_KLAVIYO_ID = 'UYRhL6';
    if (klaviyoId === PADS_KLAVIYO_ID) getAccountSubscriptions();

    setupGoogleOptimizeVariant();
  }, []);

  const setupGoogleOptimizeVariant = () => {
    if (productCode === ProductCode.TamponBox) {
      const variantId = process.env.OPTIMIZE_TAMPON_MULTI_MONTH_EXPERIMENT_ID;
      if (!variantId || typeof window === 'undefined') return;
      console.debug(`getting optimize variant for id: ${variantId}`);
      const intervalId = setInterval(() => {
        if (window.google_optimize) {
          const variant = window.google_optimize.get(variantId);
          console.debug('Google Optimize variant: ', variant);
          if (variant) setConfigVariant(variant);
          clearInterval(intervalId);
        }
      }, 100);
    }
  };

  const getAccountSubscriptions = () => {
    if (!firebaseAuth) return;
    const { currentUser } = firebaseAuth;
    if (!currentUser) return;
    const url = `/recurly/subscriptions`;
    fetch
      .get(url)
      .then((subs: Subscription[]) => {
        const tamponSubscription = subs.find((subscription) => subscription.planCode?.includes('tampon') && subscription.state === 'active');
        if (tamponSubscription) setSubscriptionId(tamponSubscription.id ?? '');
      })
      .catch((error: Error) => {
        console.error(error);
      });
  };

  return (
    <>
      <Container $textColor={textColor} $background={background} ref={divRef} $isModern={isUsingNewDesignSystem}>
        {isUsingNewDesignSystem ? (
          <ShoppingHero />
        ) : (
          <TwoColumn breakpoint="1024px" gap={columnGap} alignment="top">
            <SliderWrap>
              <ImageSlider slides={orderedSlides} />
            </SliderWrap>
            <PurchaseContainer $textColor={textColor} ref={ref}>
              <ProductDescription>
                <HeadingWrap>
                  {topSubtitleText && <TopSubtitleWrap>{topSubtitleText}</TopSubtitleWrap>}
                  {headingText && <TitleWrap>{headingText}</TitleWrap>}
                  {promoRibbon.text && <ProductPromoRibbon data={promoRibbon} outerBackground={background} />}
                  {trustpilot && <ProductHeroTrustPilot />}
                </HeadingWrap>
                {subtitle && <SubtitleWrap>{subtitle}</SubtitleWrap>}
                <TextStyled>
                  <Text text={bodyText} textColor={textColor} readMore={showReadMore} onReadMore={() => setShowReadMore(true)} maxLength={90} />
                </TextStyled>
                {icons[0]?.icon_text?.text && showReadMore && (
                  <UspsWrap>
                    <HeaderIcons icons={icons} isVerticalDirection={verticalIcons} />
                  </UspsWrap>
                )}
                {showReadMore && <StyledTextLinkButton onClick={() => setShowReadMore(false)}>{t('components.close')}</StyledTextLinkButton>}
              </ProductDescription>
              {hasFeedbackView && <HeaderFeedback feedback={feedback} />}
              <WaitlistPaymentRender />
              <ProductPromotion productId={productId} />
              <FlexPDPHoverOverlay />
              {subtextText.length > 8 && <Subtext dangerouslySetInnerHTML={{ __html: subtextText }} />}
              {filteredLinks &&
                filteredLinks.map((link, index) => (
                  <div style={{ marginTop: '15px' }} key={index}>
                    <StyledLink to={link.link?.text}>{link.link?.text}</StyledLink>
                  </div>
                ))}
              <BrightFridayBanner />
              {showNurseConsultation ? <NurseConsultationModule /> : null}
              <FlexPDPHoverOverlay />
            </PurchaseContainer>
          </TwoColumn>
        )}
      </Container>
      {stickyBanner && (
        <StickyBanner
          data={stickyBanner}
          cartItem={new CartItem(stickyBanner.productCode, stickyBanner.productCode, state.price)}
          scrollToRef={ref}
          scrollHeight={scrollHeight}
        />
      )}
    </>
  );
};

export default SharedProductHeader;

interface StyleProps {
  $textColor?: string;
  $background?: string;
  $outerBackground?: string;
  $breakpoint?: string;
  $isModern?: boolean;
}

const Container = styled.div<StyleProps>`
  padding: ${(props) => (props.$isModern ? `${NAVBAR_HEIGHT}px` : '140px 0 60px')};
  display: flex;
  justify-content: center;
  color: ${(props) => props.$textColor};
  background: ${(props) => props.$background};

  @media (max-width: 1300px) {
    padding: ${(props) => (props.$isModern ? `70px 0 0` : '140px 0 60px')};
  }

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    padding: ${(props) => (props.$isModern ? `70px 0 0` : '70px 0 30px')};
  }
`;

const SliderWrap = styled.div`
  width: 600px;
  max-width: 600px;

  @media (max-width: 1024px) {
    max-width: 100%;
    display: block;
    width: 100%;
  }
`;

const PurchaseContainer = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  max-width: 480px;
  width: 100%;
  color: ${(props) => props.$textColor};

  @media (max-width: 1300px) {
    max-width: 360px;
  }

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 480px;
    padding: 30px 20px 0;
    align-items: center;
    margin: 20px auto 0;
    gap: 20px;
  }

  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

const ProductDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;

  @media (max-width: 1024px) {
    gap: 10px;
    align-items: center;
  }
`;

const TopSubtitleWrap = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const HeadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const TitleWrap = styled(H1)`
  font-size: 36px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: inherit;

  @media (max-width: 1024px) {
    font-size: 20px;
    line-height: 140%;
    text-align: center;
  }
`;

const UspsWrap = styled.div`
  .icon-rows-wrap {
    gap: 40px;

    @media (max-width: 1200px) {
      gap: 20px;
    }
  }
  .icon-text {
    text-transform: none;
    letter-spacing: normal;
    font-size: 12px;
  }
`;

const SubtitleWrap = styled.div`
  font-family: BrownStd;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 27px;
  text-align: left;
  margin-bottom: 20px;
  color: inherit;

  @media (max-width: 1024px) {
    margin-top: 10px;

    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }
`;

const TextStyled = styled.div<StyleProps>`
  width: 100%;

  & > p {
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  @media (max-width: ${(props) => props.$breakpoint || '768px'}) {
    p,
    h2,
    h3,
    h4,
    h5,
    h6 {
      text-align: center;
    }
  }
`;

const Subtext = styled.div`
  opacity: 0.6;
  margin-top: 30px;

  p,
  h6 {
    font-family: 'BrownStd';
    font-size: 14px;
    line-height: 140%;
    color: inherit;
  }
`;

const StyledLink = styled(LinkTranslated)`
  font-family: BrownStd;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  border-bottom: 2px solid ${Colors.forestGreen};

  &:hover {
    text-decoration: none !important;
  }
`;

const StyledTextLinkButton = styled(TextLinkButton)`
  color: ${Colors.navyBlue};
  border-color: ${Colors.navyBlue};
`;
